"use client";
import { useEffect, useRef } from "react";

export interface HeroBackgroundProps {
  className?: string;
}

interface Particle {
  x: number;
  y: number;
  radius: number;
  vx: number;
  vy: number;
  type: "twitter" | "reddit" | "opportunity";
  rotation?: number;
  rotationSpeed?: number;
}

interface Ripple {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  amplitude: number;
  phase: number;
  speed: number;
}

export function HeroBackground({ className = "" }: HeroBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Load images
    const redditLogo = new Image();
    redditLogo.src = "/images/logos/reddit.svg";

    const xLogo = new Image();
    xLogo.src = "/images/logos/x.svg";

    // Set canvas dimensions
    const handleResize = () => {
      const pixelRatio = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * pixelRatio;
      canvas.height = window.innerHeight * pixelRatio;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(pixelRatio, pixelRatio);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    // Create particles
    const particles: Particle[] = [];
    const particleCount = Math.max(12, Math.floor(window.innerWidth / 120)); // Reduce overall particle count

    for (let i = 0; i < particleCount; i++) {
      // Ensure equal distribution of Reddit and X particles (50/50)
      // with no opportunity particles
      const type = i % 2 === 0 ? "reddit" : "twitter";

      // Create a safe zone in the center by positioning particles away from the center
      let x: number;
      let y: number;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const centerSafeZoneX = window.innerWidth * 0.5; // 50% of screen width
      const centerSafeZoneY = window.innerHeight * 0.5; // 50% of screen height

      // Try to position particles outside the center safe zone
      do {
        x = Math.random() * window.innerWidth;
        y = Math.random() * window.innerHeight;
      } while (
        Math.abs(x - centerX) < centerSafeZoneX / 2 &&
        Math.abs(y - centerY) < centerSafeZoneY / 2
      );

      particles.push({
        x,
        y,
        radius: 8,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        type,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.01,
      });
    }

    // Create ripples
    const ripples: Ripple[] = [];

    // Function to create a new ripple from a random position
    const createRipple = () => {
      // Random position within the canvas
      // Using margin to avoid ripples starting too close to the edges
      const margin = 100;
      const x = margin + Math.random() * (window.innerWidth - margin * 2);
      const y = margin + Math.random() * (window.innerHeight - margin * 2);

      // Generate a ripple with randomized properties
      ripples.push({
        x,
        y,
        radius: 0,
        maxRadius:
          Math.min(window.innerWidth, window.innerHeight) *
          (0.4 + Math.random() * 0.3),
        amplitude: 1 + Math.random() * 2,
        phase: 0,
        speed: 0.5 + Math.random() * 0.5,
      });

      // Limit number of active ripples
      if (ripples.length > 5) {
        ripples.shift();
      }
    };

    // Create initial ripples
    createRipple();

    // Create new ripples periodically
    const rippleInterval = setInterval(() => {
      if (Math.random() > 0.3) {
        // 70% chance to create a new ripple
        createRipple();
      }
    }, 3000);

    // Animation
    const animate = () => {
      if (!canvas || !ctx) return;

      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      // Update and draw ripples
      for (let i = 0; i < ripples.length; i++) {
        const ripple = ripples[i];

        // Update ripple
        ripple.radius += ripple.speed;
        ripple.phase += 0.02;

        // Draw ripple
        const opacity = Math.max(0, 1 - ripple.radius / ripple.maxRadius);

        // Create more realistic water-like ripple
        const rippleWidth = 2 + Math.sin(ripple.phase) * 0.5; // Slightly varying width

        // Main ripple - more transparent base state
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(125, 84, 242, ${opacity * 0.15})`; // Reduced from 0.25
        ctx.lineWidth = rippleWidth;
        ctx.stroke();

        // Subtle secondary ripple
        if (ripple.radius > 10) {
          ctx.beginPath();
          ctx.arc(ripple.x, ripple.y, ripple.radius - 8, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(125, 84, 242, ${opacity * 0.1})`; // Reduced from 0.2
          ctx.lineWidth = rippleWidth * 0.7;
          ctx.stroke();
        }

        // Very subtle tertiary ripple for more water-like effect
        if (ripple.radius > 20) {
          ctx.beginPath();
          ctx.arc(ripple.x, ripple.y, ripple.radius - 16, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(125, 84, 242, ${opacity * 0.08})`; // Reduced from 0.15
          ctx.lineWidth = rippleWidth * 0.5;
          ctx.stroke();
        }

        // Remove ripple if it has expanded beyond its max radius
        if (ripple.radius > ripple.maxRadius) {
          ripples.splice(i, 1);
          i--;
        }
      }

      // Update and draw particles
      for (let i = 0; i < particles.length; i++) {
        const particle = particles[i];

        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Calculate distance from center
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        const dx = particle.x - centerX;
        const dy = particle.y - centerY;
        const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);

        // Text-safe zone radius - apply repulsion force when particles enter this area
        const textSafeZoneRadius =
          Math.min(window.innerWidth, window.innerHeight) * 0.25;
        let centerProximityFactor = 1;

        // Apply repulsion force if particle is in the text-safe zone
        if (distanceFromCenter < textSafeZoneRadius) {
          // Calculate normalized direction vector away from center
          const repulsionStrength =
            0.08 * (1 - distanceFromCenter / textSafeZoneRadius);
          const dirX = dx / distanceFromCenter;
          const dirY = dy / distanceFromCenter;

          // Apply repulsion force
          particle.vx += dirX * repulsionStrength;
          particle.vy += dirY * repulsionStrength;

          // Limit velocity to prevent excessive speeds
          const speed = Math.sqrt(
            particle.vx * particle.vx + particle.vy * particle.vy,
          );
          if (speed > 1) {
            particle.vx = (particle.vx / speed) * 1;
            particle.vy = (particle.vy / speed) * 1;
          }

          // Reduce opacity when in text area (minimum 0.25 opacity)
          centerProximityFactor =
            0.25 + (distanceFromCenter / textSafeZoneRadius) * 0.75;
        }

        // Update rotation for logo particles
        if (particle.rotationSpeed && particle.type !== "opportunity") {
          particle.rotation! += particle.rotationSpeed;
        }

        // Check for ripple intersection - for highlight effect only, not movement
        let isBeingScanned = false;
        let scanIntensity = 0;

        for (const ripple of ripples) {
          const dx = particle.x - ripple.x;
          const dy = particle.y - ripple.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // If particle is near the ripple edge (within 20px) - highlight effect only
          const rippleEdgeDistance = Math.abs(distance - ripple.radius);
          if (rippleEdgeDistance < 15) {
            isBeingScanned = true;
            // Calculate intensity based on how close to the ripple edge
            const intensity = 1 - rippleEdgeDistance / 15;
            scanIntensity = Math.max(scanIntensity, intensity);
          }
        }

        // Bounce off edges with some margin
        const margin = 50;
        if (particle.x < margin || particle.x > window.innerWidth - margin)
          particle.vx *= -1;
        if (particle.y < margin || particle.y > window.innerHeight - margin)
          particle.vy *= -1;

        // Keep within bounds
        particle.x = Math.max(
          margin,
          Math.min(window.innerWidth - margin, particle.x),
        );
        particle.y = Math.max(
          margin,
          Math.min(window.innerHeight - margin, particle.y),
        );

        // Draw connections between nearby particles
        for (let j = i + 1; j < particles.length; j++) {
          const otherParticle = particles[j];
          const dx = otherParticle.x - particle.x;
          const dy = otherParticle.y - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Draw lines between particles that are close enough
          if (distance < 120) {
            const opacity = (1 - distance / 120) * 0.08; // Reduced from 0.15

            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);

            // Different connection colors based on particle types with reduced base opacity
            if (
              particle.type === "twitter" &&
              otherParticle.type === "twitter"
            ) {
              ctx.strokeStyle = `rgba(0, 0, 0, ${opacity * 0.8})`; // Black for X connections
            } else if (
              particle.type === "reddit" &&
              otherParticle.type === "reddit"
            ) {
              ctx.strokeStyle = `rgba(255, 69, 0, ${opacity * 0.8})`; // Reddit orange
            } else {
              ctx.strokeStyle = `rgba(200, 200, 200, ${opacity * 0.3})`; // More subtle mixed connections
            }

            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }

        // Apply shadow effects for highlighting
        if (isBeingScanned) {
          ctx.shadowColor =
            particle.type === "twitter"
              ? "rgba(0, 0, 0, 0.5)" // Black shadow for X
              : particle.type === "reddit"
                ? "rgba(255, 69, 0, 0.5)"
                : "rgba(125, 84, 242, 0.5)";
          ctx.shadowBlur = 5 + 10 * scanIntensity;
        } else if (particle.type === "opportunity") {
          ctx.shadowColor = "rgba(125, 84, 242, 0.5)";
          ctx.shadowBlur = 5;
        }

        // Draw particle based on type
        if (particle.type === "reddit" && redditLogo.complete) {
          const size = particle.radius * 1.5;
          ctx.save();
          ctx.translate(particle.x, particle.y);
          if (particle.rotation) ctx.rotate(particle.rotation);

          // Base opacity is lower, but full opacity when intersecting with ripples
          const baseOpacity = 0.4; // Reduced from 0.8
          const opacity = isBeingScanned ? 1 : baseOpacity;
          ctx.globalAlpha = opacity;

          ctx.drawImage(redditLogo, -size, -size, size * 2, size * 2);
          ctx.restore();
        } else if (particle.type === "twitter" && xLogo.complete) {
          const size = particle.radius * 1.5;
          ctx.save();
          ctx.translate(particle.x, particle.y);
          if (particle.rotation) ctx.rotate(particle.rotation);

          // Base opacity is lower, but full opacity when intersecting with ripples
          const baseOpacity = 0.4; // Reduced from 0.8
          const opacity = isBeingScanned ? 1 : baseOpacity;
          ctx.globalAlpha = opacity;

          ctx.drawImage(xLogo, -size, -size, size * 2, size * 2);
          ctx.restore();
        } else {
          // Draw opportunity particle as a circle with enhanced design
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);

          // Set color based on scan intensity
          if (isBeingScanned) {
            ctx.fillStyle = `rgba(${125 + 40 * scanIntensity}, ${84 + 40 * scanIntensity}, ${242}, ${0.9 + 0.1 * scanIntensity})`;
          } else {
            ctx.fillStyle = "rgba(125, 84, 242, 0.9)";
          }

          ctx.fill();

          // Add a subtle ring around opportunity particles
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.radius + 1, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(125, 84, 242, 0.3)";
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }

        // Reset shadow
        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
      }

      requestAnimationFrame(animate);
    };

    const animationId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
      clearInterval(rippleInterval);
    };
  }, []);

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      {/* Canvas for particles and ripples */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
    </div>
  );
}
